import {Dispatch, SetStateAction, useMemo, useState} from 'react';

import {CoParentInvitation} from '@/app/main/coparent/interfaces';
import {
  getCoParentInvitationFromStorage,
  getInvitationFirstName,
  getInvitationInviterName,
  getInvitationLastName,
  normalizeInvitationPhone,
} from '@/app/main/coparent/utils';
import {isDevelopment} from '@/utils/environment';
import {
  generateRandomEmail,
  generateRandomPhoneNumber,
} from '@/utils/randomGenerator';

import {useDevAutofillNames} from './useDevAutofillNames';

interface SignUpNavigationData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

interface UseSignUpPersonalInfoResult {
  invitation: CoParentInvitation | null;
  firstName: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  lastName: string;
  setLastName: Dispatch<SetStateAction<string>>;
  phone: string;
  setPhone: Dispatch<SetStateAction<string>>;
  isPhonePrefilledFromInvitation: boolean;
  coParentInviterName?: string;
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
}

// Seeds the personal-info fields (name, phone, email) from a pending
// co-parent invitation first, then from data the previous step passed via
// router state, falling back to random dev-only values so manual testing
// doesn't require typing them every time.
export function useSignUpPersonalInfo(
  signUpData: SignUpNavigationData,
): UseSignUpPersonalInfoResult {
  const invitation = useMemo(() => getCoParentInvitationFromStorage(), []);

  const [firstName, setFirstName] = useState<string>(() => {
    const invitationFirstName = invitation
      ? getInvitationFirstName(invitation)
      : undefined;

    if (invitationFirstName) return invitationFirstName;
    if (signUpData.firstName) return signUpData.firstName;
    return '';
  });
  const [lastName, setLastName] = useState<string>(() => {
    const invitationLastName = invitation
      ? getInvitationLastName(invitation)
      : undefined;

    if (invitationLastName) return invitationLastName;
    if (signUpData.lastName) return signUpData.lastName;
    return '';
  });

  useDevAutofillNames({
    invitation,
    setFirstName,
    setLastName,
  });

  const [phone, setPhone] = useState<string>(() => {
    if (invitation?.phone) {
      return normalizeInvitationPhone(invitation.phone);
    }

    return (
      signUpData.phone ||
      (isDevelopment ? generateRandomPhoneNumber() : undefined)
    );
  });
  const [isPhonePrefilledFromInvitation] = useState<boolean>(
    () => !!invitation?.phone,
  );
  const [coParentInviterName] = useState<string | undefined>(() =>
    invitation ? getInvitationInviterName(invitation) : undefined,
  );

  const [email, setEmail] = useState<string>(
    signUpData.email || (isDevelopment ? generateRandomEmail() : undefined),
  );

  return {
    invitation,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,
    isPhonePrefilledFromInvitation,
    coParentInviterName,
    email,
    setEmail,
  };
}
